import React, { useState } from 'react';

import { CmModal } from '@/components/uiParts';
import { PreviewBrandItems } from '@/pages/menu-managements/items/components/UberEatsImportBrandItemsModal/PreviewBrandItems';
import useMenuManagementBulkUpsertBrandItems, {
  fromPreviewBrandItem,
} from '@/services/BrandItem/hooks/upload/useMenuManagementBulkUpsertBrandItems';
import useMenuManagementUberPreviewBulkUpsertBrandItems, {
  UberPreviewBulkUpsertBrandItemsResponse,
} from '@/services/BrandItem/hooks/upload/useMenuManagementUberPreviewBulkUpsertBrandItems';

import { MasterStoreSelect } from './MasterStoreSelect';

type Props = {
  isOpen: boolean;
  associateBrandId: string;
  handleCloseModal: () => void;
  onImportBrandItems: () => void;
};

export const UberEatsImportBrandItemsModal: React.FC<Props> = (props) => {
  const [selectedMasterStore, setSelectedMasterStore] = useState<
    undefined | { id: number; name: string }
  >();
  const { loading: isPreviewLoading, preview } =
    useMenuManagementUberPreviewBulkUpsertBrandItems();
  const [previewResponse, setPreviewResponse] = useState<
    UberPreviewBulkUpsertBrandItemsResponse | undefined
  >();
  const { loading: isImportLoading, upload: importBrandItems } =
    useMenuManagementBulkUpsertBrandItems();

  const onClose = () => {
    setSelectedMasterStore(undefined);
    setPreviewResponse(undefined);
    props.handleCloseModal();
  };

  const isPreviewButtonDisabled = !selectedMasterStore;
  const onClickPreview = async () => {
    if (!selectedMasterStore) return;

    const response = await preview({
      associateBrandId: Number.parseInt(props.associateBrandId, 10),
      masterStoreId: selectedMasterStore.id,
    });
    setPreviewResponse(response);
  };

  const isImportDisabled = !previewResponse;
  const onClickImport = async () => {
    if (!previewResponse) return;

    await importBrandItems({
      associateBrandId: Number.parseInt(props.associateBrandId, 10),
      brandItems: previewResponse.brand_items.map(fromPreviewBrandItem),
      brandCategories: previewResponse.brand_categories,
      brandOptionGroups: previewResponse.brand_option_groups,
      uberCategoryIdsByUberMenuItemId:
        previewResponse.uber_category_ids_by_uber_menu_item_id,
      uberModifierGroupIdsByUberMenuItemId:
        previewResponse.uber_modifier_group_ids_by_uber_menu_item_id,
    });
    props.onImportBrandItems();
    onClose();
  };

  return (
    <CmModal
      size="5xl"
      title="uber eatsデータインポート"
      isOpen={props.isOpen}
      onClose={onClose}
    >
      {previewResponse ? (
        <PreviewBrandItems
          masterStoreName={selectedMasterStore?.name ?? ''}
          insertCount={previewResponse.insert_count}
          updateCount={previewResponse.update_count}
          errorsByLine={previewResponse.errors_by_line}
          brandItems={previewResponse.brand_items}
          brandCategories={previewResponse.brand_categories}
          brandOptionGroups={previewResponse.brand_option_groups}
          uberCategoryIdsByUberMenuItemId={
            previewResponse.uber_category_ids_by_uber_menu_item_id
          }
          uberModifierGroupIdsByUberMenuItemId={
            previewResponse.uber_modifier_group_ids_by_uber_menu_item_id
          }
          onClickCancel={onClose}
          isImportDisabled={isImportDisabled}
          isImportLoading={isImportLoading}
          onClickImport={onClickImport}
        />
      ) : (
        <MasterStoreSelect
          selectedMasterStoreId={selectedMasterStore?.id}
          onChangeMasterStore={setSelectedMasterStore}
          onClose={onClose}
          onClickPreview={onClickPreview}
          isPreviewButtonDisabled={isPreviewButtonDisabled}
          isPreviewButtonLoading={isPreviewLoading}
        />
      )}
    </CmModal>
  );
};
